import React, {SyntheticEvent, useEffect, useState} from "react";
import {create, getById, getProfileByUserId, setPassword, update} from "../../services/user.service";
import {NavigateFunction, NavLink, useNavigate, useParams} from "react-router-dom";
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {ArrowBackIosNew} from "@mui/icons-material";
import IUser from "../../types/user.type";
import showNotification from "../utils/Notify";
import {Box, Breadcrumbs, Button, Grid, Stack, Tab, TabList, TabPanel, Tabs, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import {getRoles} from "../../services/role.service";
import IRole from "../../types/role.type";
import PersonalDataForm from "../profile/PersonalDataForm";
import AddressDataForm from "../profile/AddressDataForm";
import BankDataForm from "../profile/BankDataForm";
import EmploymentDataForm from "../profile/EmploymentDataForm";
import IProfile from "../../types/profile.type";
import RelativesDataForm from "../profile/RelativesDataForm";
import LockResetIcon from '@mui/icons-material/LockReset';
import SetPasswordModal from "./SetPasswordModal";
import ISetPassword from "../../types/set-password.type";
import FileDataForm from "../profile/FileDataForm";
import {ProfileFileNames} from "../../types/enumeration/ProfileFileNames";

const UserForm = () => {
  let navigate: NavigateFunction = useNavigate();
  const { id, selectedTab } = useParams();
  const [roles, setRoles] = useState<Array<IRole>>();
  const [profile, setProfile] = useState<IProfile>();
  const [setPasswordModalOpen, setSetPasswordModalOpen] = React.useState(false);

  //Change password modal methods
  const handleSetPasswordModalClose = () => {
    setSetPasswordModalOpen(false);
  }

  const handleChangePasswordClick = () => {
    setSetPasswordModalOpen(true);
  };

  const onSubmitSetPassword = (password: ISetPassword) => {
    setPassword(id, password)
      .then((response) => {
        showNotification({type: 'success', message: 'Contraseña actualizada correctamente.'});
        handleSetPasswordModalClose();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  }

  const initialValues: IUser = {
    id: null,
    username: '',
    email: '',
    roleId: null,
    roleName: null,
    enabled: 'true',
    name: null,
    lastName: null,
    department: '',
    roles: null,
    profileImage: null
  };

  const validationSchema = Yup.object({
    username: Yup.string()
                 .min(5, "El nombre de usuario es demasiado corto. Debe tener un minimo de 5 caracteres.")
                 .max(20, "El nombre de usuario es demasiado largo. Debe tener un maximo de 20 caracteres.")
                 .matches(/^[A-Za-z0-9_-]+$/, 'El nombre de usuario solo puede contener letras (a-z, A-Z), ' +
                   'numeros (0-9), guion bajo (_) y guion medio (-).')
                 .required('El nombre de usuario es requerido'),
    email: Yup.string().email('Email is invalid').required('El Email es requerido'),
    roleId: Yup.string().required('El Rol es requerido'),
    enabled: Yup.string().required('Es necesario indicar si la cuenta esta habilitada'),
    department: Yup.string().required('Es necesario indicar el sector')
  });

  const onSubmit = (user: IUser) => {
    if (id) {
      update(user.id, user)
        .then(() => {
          showNotification({type: 'success', message: 'User updated successfully.'});
          navigate('/users')
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(user)
        .then((response) => {
          showNotification({type: 'success', message: 'User created successfully.'});
          navigate('/users/edit/' + response.data.id + '/user-account')
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const handleTabChange = (event: SyntheticEvent<Element, Event> | null, value: string | number | null) => {
    let uri = window.location.pathname;
    navigate(uri.substring(0, uri.lastIndexOf('/') + 1) + value);
  };

  const handleChangeEnabled = (event: SelectChangeEvent) => {
    formik.setFieldValue('enabled', event.target.value)
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const goBack = () => {
    navigate(-1)
  };

  const handleChangeRole = (event: SelectChangeEvent) => {
    formik.setFieldValue('roleId', event.target.value)
  };

  const handleChangeDepartment = (event: SelectChangeEvent) => {
    formik.setFieldValue('department', event.target.value)
  };

  const fetchProfile = async () => {
    if (id) {
      const profileResponse = await getProfileByUserId(id);
      if (profileResponse.status != 204) {
        setProfile( profileResponse.data);
      } else {
        setProfile(undefined);
      }
    }
  };

  const fetchUser = async () => {
    if (id) {
      const userResponse = await getById(id);
      formik.setValues(userResponse.data);
    }
  };

  const fetchRoles = async () => {
    getRoles({page: 0, size: 100, sort: "id", order: "asc"}).then( (response) => {
      setRoles(response.data.content)
    })
  };

  useEffect(() => {
    fetchRoles();
    fetchUser();
    fetchProfile();
  }, [selectedTab]);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <ManageAccountsIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  component={NavLink}
                  to="/users"
                  underline={"none"}
                >
                  Usuarios y Legajos
                </Link>
              </Typography>
              <Typography level="h4" component="h1">
                {id ? "Editar" : "Nuevo"}
              </Typography>
            </Breadcrumbs>
          </Box>

          <SetPasswordModal
            isOpen={setPasswordModalOpen}
            onSubmit={onSubmitSetPassword}
            onClose={handleSetPasswordModalClose}
          />

          <Box sx={{ width: '100%', height: 672, typography: 'body1' }} ml={1} mt={2} pr={2}>
            <Tabs defaultValue={selectedTab ? selectedTab : "booking"} onChange={handleTabChange}>
              <TabList>
                <Tab value="user-account">Cuenta de Usuario</Tab>
                <Tab value="user-profile" disabled={!id}>Legajo del Usuario</Tab>
              </TabList>
              <TabPanel value={"user-account"} sx={{padding: 0}}>
                <form className="form" onSubmit={formik.handleSubmit}>
                  <Grid container rowSpacing={2} marginBlock={1}>
                    <Grid xl={12} md={12} sm={12} xs={12}>
                      <CssVarsProvider>
                        <TextField
                          fullWidth
                          id="id"
                          label="Nombre de Usuario"
                          name="username"
                          variant="filled"
                          type={"text"}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.username}
                          error={!!(formik.touched.username && formik.errors.username)}
                          slotProps={{ inputLabel: {shrink: true}}}
                        />
                        {formik.touched.username && formik.errors.username ? (
                          <span className="Error-alert">{formik.errors.username}</span>
                        ) : null}
                      </CssVarsProvider>
                    </Grid>
                    <Grid xl={12} md={12} sm={12} xs={12}>
                      <CssVarsProvider>
                        <TextField
                          fullWidth
                          id="email"
                          label="Email"
                          name="email"
                          variant="filled"
                          type={"email"}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.email}
                          error={!!(formik.touched.username && formik.errors.username)}
                          slotProps={{ inputLabel: {shrink: true}}}
                        />
                        {formik.touched.email && formik.errors.email ? (
                          <span className="Error-alert">{formik.errors.email}</span>
                        ) : null}
                      </CssVarsProvider>
                    </Grid>
                    <Grid xl={12} md={12} sm={12} xs={12}>
                      <CssVarsProvider>
                        <FormControl variant="filled" fullWidth>
                          <InputLabel id="role-label" shrink>Rol</InputLabel>
                          <Select
                            labelId="role-label"
                            id="role"
                            name="role"
                            label="role"
                            value={formik.values.roleId}
                            onBlur={formik.handleBlur}
                            onChange={handleChangeRole}
                          >
                            {roles?.map((role) => (
                              <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                            ))}
                          </Select>
                          {formik.touched.roleId && formik.errors.roleId ? (
                            <span className="Error-alert">{String(formik.errors.roleId)}</span>
                          ) : null}
                        </FormControl>
                      </CssVarsProvider>
                    </Grid>
                    <Grid xl={12} md={12} sm={12} xs={12}>
                      <CssVarsProvider>
                        <FormControl variant="filled" fullWidth>
                          <InputLabel id="lockCalendar-label" shrink>Sector</InputLabel>
                          <Select
                            labelId="enabled-label"
                            id="department"
                            name="department"
                            label="department"
                            value={formik.values.department}
                            onBlur={formik.handleBlur}
                            onChange={handleChangeDepartment}
                          >
                            <MenuItem key="ADMINISTRATION" value="ADMINISTRATION">Administracion</MenuItem>
                            <MenuItem key="OPERATOR" value="OPERATOR">Operaciones</MenuItem>
                          </Select>
                          {formik.touched.department && formik.errors.department ? (
                            <span className="Error-alert">{formik.errors.department}</span>
                          ) : null}
                        </FormControl>
                      </CssVarsProvider>
                    </Grid>
                    <Grid xl={12} md={12} sm={12} xs={12}>
                      <CssVarsProvider>
                        <FormControl variant="filled" fullWidth>
                          <InputLabel id="lockCalendar-label" shrink>Habilitada</InputLabel>
                          <Select
                            labelId="enabled-label"
                            id="enabled"
                            name="enabled"
                            label="enabled"
                            value={formik.values.enabled}
                            onBlur={formik.handleBlur}
                            onChange={handleChangeEnabled}
                          >
                            <MenuItem key="true" value="true">Si</MenuItem>
                            <MenuItem key="false" value="false">No</MenuItem>
                          </Select>
                          {formik.touched.enabled && formik.errors.enabled ? (
                            <span className="Error-alert">{formik.errors.enabled}</span>
                          ) : null}
                        </FormControl>
                      </CssVarsProvider>
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" justifyContent="flex-start">
                      {id &&
                        <Button variant="solid" color="primary" endDecorator={<LockResetIcon />} onClick={handleChangePasswordClick}>
                          Cambiar Contraseña
                        </Button>
                      }
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" spacing={2} >
                      <Button variant="outlined" endDecorator={<ArrowBackIosNew/>} onClick={goBack}>Volver</Button>
                      <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon/>}>Guardar</Button>
                    </Stack>
                  </Stack>

                </form>
              </TabPanel>
              <TabPanel value={"user-profile"} sx={{padding: 0, paddingTop: 1, paddingBottom: 2}}>

                <PersonalDataForm userId={id} profile={profile} />

                <AddressDataForm userId={id} profile={profile}  />

                <BankDataForm userId={id} profile={profile} />

                <RelativesDataForm userId={id} profile={profile} />

                <EmploymentDataForm userId={id} profile={profile} canEdit={true}/>

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.curriculum}
                  profileFileName={ProfileFileNames.CURRICULUM}
                  title={"Curriculum Vitae"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.medicalExam}
                  profileFileName={ProfileFileNames.MEDICAL_EXAM}
                  title={"Examen Medico"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.epp}
                  profileFileName={ProfileFileNames.EPP}
                  title={"EPP"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.dni}
                  profileFileName={ProfileFileNames.DNI}
                  title={"DNI"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.cuil}
                  profileFileName={ProfileFileNames.CUIL}
                  title={"CUIL"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.drivingLicense}
                  profileFileName={ProfileFileNames.DRIVING_LICENCE}
                  title={"Licencia de Conducir"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

                <FileDataForm
                  userId={id}
                  profile={profile}
                  profileFile={profile?.passport}
                  profileFileName={ProfileFileNames.PASSPORT}
                  title={"Pasaporte"}
                  onProfileUpdated={(p) => setProfile(p)}
                />

              </TabPanel>
            </Tabs>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserForm;
